import React from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import Zoom from '@mui/material/Zoom'
import { useIntl } from 'react-intl'

import { getFlagAssetForLanguageId } from '../../../Logic/FlagUnicodeLogic'
import DefaultCourseGroupImage from '../../../assets/images/courseGroup/FallbackCourseGroupImage.jpeg'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { CourseGroupV1Dto } from '../../../Logic/StudentCourseDtos'
import { useStudentImage } from '../useStudentImage'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        width: 264,
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 300ms ease-in-out, transform 200ms ease',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        '&:hover': {
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.14)',
            transform: 'translateY(-2px)',
        },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 340,
        backgroundColor: '#FFFFFF',
        '@media (max-width: 599px)': {
            width: '100%',
            minHeight: 0,
        },
    },
    imageContainer: {
        position: 'relative',
        width: '100%',
        height: 148,
        overflow: 'hidden',
        backgroundColor: CapeeshColors.Gray5,
    },
    courseImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
    },
    flagBadge: {
        position: 'absolute' as const,
        top: 10,
        right: 10,
        width: 32,
        height: 24,
        borderRadius: 4,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column' as const,
        flexGrow: 1,
        padding: '16px 16px 20px 16px',
    },
    courseName: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '140%',
        color: CapeeshColors.DarkText,
        marginBottom: 6,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    courseDescription: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '150%',
        color: CapeeshColors.Gray1,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flexGrow: 1,
    },
    languagesRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 6,
        marginTop: 'auto',
        paddingTop: 10,
        flexWrap: 'wrap' as const,
    },
    languageChip: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 400,
        color: CapeeshColors.Primary900,
        backgroundColor: CapeeshColors.Primary100,
        borderRadius: 12,
        padding: '2px 8px',
        whiteSpace: 'nowrap' as const,
    },
    imagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: CapeeshColors.Gray5,
    },
    progressionContainer: {
        padding: '0 16px 16px 16px',
    },
    progressionRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 8,
    },
    progressionBarBackground: {
        flex: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: CapeeshColors.Gray5,
        overflow: 'hidden',
    },
    progressionBarFill: {
        height: '100%',
        borderRadius: 3,
        transition: 'width 600ms ease-in-out',
    },
    progressionLabel: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 500,
        color: CapeeshColors.Gray1,
        minWidth: 36,
        textAlign: 'right' as const,
    },
}))

interface StudentCourseGroupCardProps {
    courseGroup: CourseGroupV1Dto
    normalizedProgression: number | null
    onSelect: (courseGroupId: string) => void
    index: number
    l2Only?: boolean
}

const StudentCourseGroupCard: React.FC<StudentCourseGroupCardProps> = ({
    courseGroup,
    normalizedProgression,
    onSelect,
    index,
    l2Only = false,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const imageBlobUrl = useStudentImage(courseGroup?.imageUrl)

    if (!courseGroup) {
        return null
    }

    const name: string = l2Only
        ? (courseGroup.l2Name || intl.formatMessage(studentScreenMessages.courseFallback))
        : (courseGroup.l1Name || courseGroup.l2Name || intl.formatMessage(studentScreenMessages.courseFallback))
    const description: string = l2Only
        ? (courseGroup.l2Description || '')
        : (courseGroup.l1Description || courseGroup.l2Description || '')
    const l2Id: number = courseGroup.l2Id

    const handleClick = (): void => {
        if (onSelect) {
            onSelect(courseGroup.id)
        }
    }

    // Extract total topic count from sub parts
    let topicCount = 0
    if (courseGroup.courseGroupSubParts) {
        courseGroup.courseGroupSubParts.forEach((subPart) => {
            if (subPart.courses) {
                topicCount += subPart.courses.length
            }
        })
    }

    return (
        <Zoom in={true} style={{ transitionDelay: `${(index || 0) * 60}ms` }}>
            <Card className={classes.cardRoot} onClick={handleClick}>
                <div className={classes.imageContainer}>
                    {imageBlobUrl ? (
                        <img
                            src={imageBlobUrl}
                            className={classes.courseImage}
                            alt={name}
                        />
                    ) : (
                        <img
                            src={DefaultCourseGroupImage}
                            className={classes.courseImage}
                            alt={name}
                        />
                    )}
                    {l2Id && (
                        <img
                            src={getFlagAssetForLanguageId(l2Id)}
                            className={classes.flagBadge}
                            alt="Language"
                        />
                    )}
                </div>

                <div className={classes.cardBody}>
                    <Typography className={classes.courseName}>
                        {name}
                    </Typography>

                    {description && (
                        <Typography className={classes.courseDescription}>
                            {description}
                        </Typography>
                    )}

                    {!description && topicCount > 0 && (
                        <Typography className={classes.courseDescription}>
                            {intl.formatMessage(studentScreenMessages.topicCount, { count: topicCount })}
                        </Typography>
                    )}

                    {courseGroup.courseGroupSubParts &&
                        courseGroup.courseGroupSubParts.length > 0 &&
                        courseGroup.courseGroupSubParts[0]?.courses?.length > 0 && (
                            <div className={classes.languagesRow}>
                                {courseGroup.courseGroupSubParts[0].courses
                                    .slice(0, 3)
                                    .map((course, idx) => (
                                        <span key={idx} className={classes.languageChip}>
                                            {course.l2TopicName || course.l1TopicName}
                                        </span>
                                    ))}
                                {courseGroup.courseGroupSubParts[0].courses.length > 3 && (
                                    <span className={classes.languageChip}>
                                        +{courseGroup.courseGroupSubParts[0].courses.length - 3}
                                    </span>
                                )}
                            </div>
                        )}
                </div>

                {normalizedProgression != null && (
                    <div className={classes.progressionContainer}>
                        <div className={classes.progressionRow}>
                            <div className={classes.progressionBarBackground}>
                                <div
                                    className={classes.progressionBarFill}
                                    style={{
                                        width: `${Math.min(Math.round(normalizedProgression * 100), 100)}%`,
                                        backgroundColor:
                                            normalizedProgression >= 0.999
                                                ? CapeeshColors.GreenBright
                                                : CapeeshColors.Primary600,
                                    }}
                                />
                            </div>
                            <Typography className={classes.progressionLabel}>
                                {Math.round(normalizedProgression * 100)}%
                            </Typography>
                        </div>
                    </div>
                )}
            </Card>
        </Zoom>
    )
}

export default StudentCourseGroupCard
